import { Turnstile } from "@marsidev/react-turnstile"
import {
  ActionLink,
  ActionSubmit,
  Form,
  LargeInput,
  useToasterContext,
  ValidationContainer,
  ValidationMessage,
} from "@tandemdiabetes/sugarmate-react-components"
import { Link } from "@tanstack/react-router"
import type {
  ChangeEventHandler,
  FormEventHandler,
} from "react"
import {
  useState,
  useCallback,
} from "react"
import styles from "./ForgotPassword.module.scss"
import { useForgotPassword } from "./useForgotPassword"
import {
  CountryProtectedPage,
  PublicPageHeader,
} from "src/components"
import { usePolyglot } from "src/contexts"
import { ForgotPasswordErrorToast } from "src/toasts"
import type { RoutePath } from "src/types"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

const emailId = "user-email-input"
const turnstile = "turnstile-button"
const resetButton = "reset-password-button"
const polyglotPrefix = "pages.forgot_password."

const emailErrorId = "user-email-input-error"

interface Props {
  backRoute: RoutePath;
  doneRoute: RoutePath;
}

export function ForgotPasswordContent(props: Props) {
  const {
    backRoute,
    doneRoute,
  } = props

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const forgotPassword = useForgotPassword()

  const [
    sentEmail,
    setSentEmail,
  ] = useState<boolean>(false)

  const [
    email,
    setEmail,
  ] = useState<string>("")

  const [
    token,
    setToken,
  ] = useState<string>("")

  const onTurnstileSuccess = useCallback(
    (newToken: string) => {
      setToken(newToken)
    },
    [setToken],
  )

  /*
   * the Turnstile component does not respond to changes on this callback
   * so we are passing a ref for it to access the current state
   */
  const onTurnstileError = useCallback(
    () => {
      setToken("")
    },
    [setToken],
  )

  const onEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setEmail(e.target.value)
    },
    [setEmail],
  )

  const onSubmit: FormEventHandler = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        try {
          if (!forgotPassword) {
            throw new Error("cannot call forgotPassword")
          }

          await forgotPassword(
            email,
            token,
          )

          setSentEmail(true)
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            ForgotPasswordErrorToast,
            {
              code: sugarmateError?.code,
              status: sugarmateError?.status,
            },
          )

          showToast(
            "forgot-password-email-error",
            ErrorToast,
          )
        }
      })()
    },
    [
      email,
      token,
      forgotPassword,
      showToast,
      setSentEmail,
    ],
  )

  return (
    <CountryProtectedPage>
      <div className={styles.container}>
        <PublicPageHeader backRoutePath={backRoute}>
          {polyglot.t(`${polyglotPrefix}title`)}
        </PublicPageHeader>
        <Form
          className={styles.form}
          onSubmit={onSubmit}
          id="emailsentMessage"
        >
          {sentEmail ? (
            <>
              <p>
                {polyglot.t(
                  `${polyglotPrefix}success_message`,
                  { email },
                )}
              </p>
              <ActionLink
                className={styles.actionLink}
                stylePreset="secondary"
                asChild
              >
                <Link
                  to={doneRoute}
                  params={{}}
                  search={{}}
                >
                  {polyglot.t(`${polyglotPrefix}done_cta`)}
                </Link>
              </ActionLink>
            </>
          ) : (
            <>
              <ValidationContainer>
                <LargeInput
                  id={emailId}
                  placeholder={polyglot.t(`${polyglotPrefix}email.placeholder`)}
                  className={styles.input}
                  type="email"
                  value={email}
                  onChange={onEmailChange}
                  required
                />
                <ValidationMessage
                  id={emailErrorId}
                  invalidMessage={{
                    valueMissing: polyglot.t("validation_message.value_missing"),
                    typeMismatch: polyglot.t(
                      "validation_message.email_type_mismatch",
                    ),
                  }}
                />
              </ValidationContainer>
              <Turnstile
                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY ?? ""}
                className={styles.turnstile}
                onSuccess={onTurnstileSuccess}
                onError={onTurnstileError}
                onExpire={onTurnstileError}
                id={turnstile}
              />
              <ActionSubmit
                id={resetButton}
                value={polyglot.t(`${polyglotPrefix}submit_cta`)}
                stylePreset="primary"
                className={styles.submit}
                disabled={!token || !forgotPassword}
              />
            </>
          )}
        </Form>
      </div>
    </CountryProtectedPage>
  )
}
